import React, { useState, useEffect } from "react";
import { Sparkles, TrendingUp, AlertTriangle, CheckCircle, RefreshCw, Calendar, Target, Twitter, Heart, Repeat, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import AppLayout from "@/components/AppLayout";
import AIModeSelector from "@/components/ai/AIModeSelector";
import { useAISettings } from "@/components/ai/aiRoutes";

export default function AIManager() {
  const [mode, setMode] = useState("safe");
  const { settings, reload: reloadSettings } = useAISettings();
  const [briefData, setBriefData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tweetSuggestions, setTweetSuggestions] = useState([]);
  const [loadingTweets, setLoadingTweets] = useState(false);
  const [reposting, setReposting] = useState(null);
  const [planPeriod, setPlanPeriod] = useState("weekly");
  const [planProposals, setPlanProposals] = useState([]);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [planActionId, setPlanActionId] = useState(null);

  useEffect(() => {
    loadData();
  }, [mode]);

  const changeMode = (next) => {
    if (next === mode) return;
    setSuggestions(null);
    setMode(next);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [briefRes, alertsRes, plansRes] = await Promise.all([
        api.get("/api/ai-manager/daily-brief", { params: { mode } }),
        api.get("/api/ai-manager/alerts"),
        api.get("/api/ai-manager/plans").catch(() => ({ data: { proposals: [] } })),
      ]);
      
      if (briefRes.data.ok) {
        setBriefData(briefRes.data);
      }
      setAlerts(alertsRes.data.alerts || []);
      setPlanProposals(plansRes.data.proposals || []);
    } catch (e) {
      toast.error("Failed to load AI Manager data");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const generatePlan = async () => {
    setGeneratingPlan(true);
    try {
      const { data } = await api.post("/api/ai-manager/plans/generate", { period: planPeriod, mode });
      setPlanProposals((current) => [data.proposal, ...current]);
      toast.success(`${planPeriod === "weekly" ? "Weekly" : "Monthly"} plan ready for review`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate plan");
    } finally {
      setGeneratingPlan(false);
    }
  };

  const actOnPlan = async (proposal, action) => {
    setPlanActionId(proposal.id);
    try {
      const { data } = await api.post(`/api/ai-manager/plans/${proposal.id}/${action}`);
      if (action === "approve") {
        toast.success(`Approved: ${data.planner_events} calendar items and ${data.post_drafts} post drafts`);
      } else {
        toast.success("Plan rejected");
      }
      const refreshed = await api.get("/api/ai-manager/plans");
      setPlanProposals(refreshed.data.proposals || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action} plan`);
    } finally {
      setPlanActionId(null);
    }
  };

  const refreshBrief = async () => {
    setRefreshing(true);
    try {
      // Explicit Refresh is the only thing that regenerates today's cached brief.
      const res = await api.get("/api/ai-manager/daily-brief", { params: { mode, refresh: true } });
      if (res.data.ok) {
        setBriefData(res.data);
        toast.success("Daily brief refreshed!");
      }
    } catch (e) {
      toast.error("Failed to refresh brief");
    } finally {
      setRefreshing(false);
    }
  };

  const generateSuggestions = async () => {
    try {
      toast.info("Generating content ideas...");
      const res = await api.post("/api/ai-manager/content-suggestions", null, { params: { mode } });
      if (res.data.ok) {
        setSuggestions(res.data);
        toast.success("Content ideas generated!");
      }
    } catch (e) {
      toast.error("Failed to generate suggestions");
      console.error(e);
    }
  };

  const loadTweetSuggestions = async () => {
    try {
      setLoadingTweets(true);
      toast.info("Analyzing your top tweets...");
      const res = await api.get("/api/ai-manager/suggest-tweets-to-repost?days=30&min_engagement=5");
      
      if (res.data.ok) {
        setTweetSuggestions(res.data.suggestions || []);
        if (res.data.suggestions.length === 0) {
          toast.info(res.data.message || "No high-engagement tweets found");
        } else {
          toast.success(`Found ${res.data.suggestions.length} top tweets to repost!`);
        }
      } else {
        toast.error(res.data.error || "Failed to load tweet suggestions");
      }
    } catch (e) {
      toast.error("Failed to analyze tweets");
      console.error(e);
    } finally {
      setLoadingTweets(false);
    }
  };

  const handleRepostTweet = async (tweet) => {
    try {
      setReposting(tweet.tweet_id);
      const res = await api.post("/api/ai-manager/repost-tweet", {
        tweet_id: tweet.tweet_id,
        new_caption: tweet.suggested_caption,
        quote_original: true
      });
      
      if (res.data.ok) {
        toast.success("Saved as a draft in the X queue — nothing was posted.");
        // Remove from suggestions
        setTweetSuggestions(prev => prev.filter(t => t.tweet_id !== tweet.tweet_id));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to repost tweet");
      console.error(e);
    } finally {
      setReposting(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case "critical": return "bg-red-900/20 border-red-800/30 text-red-400";
      case "high": return "bg-orange-900/20 border-orange-800/30 text-orange-400";
      case "medium": return "bg-yellow-900/20 border-yellow-800/30 text-yellow-400";
      default: return "bg-blue-900/20 border-blue-800/30 text-blue-400";
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <RefreshCw className="animate-spin text-blue-400" size={32} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Sparkles className="text-purple-400" size={32} />
              AI Business Manager
            </h1>
            <p className="text-zinc-400 mt-1">Daily strategy briefs, alerts, and proactive insights</p>
            <div className="mt-3">
              <AIModeSelector mode={mode} onChange={changeMode} settings={settings} />
            </div>
          </div>
          <button
            onClick={refreshBrief}
            disabled={refreshing}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-2 font-medium transition disabled:opacity-50"
          >
            <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
            Refresh Brief
          </button>
        </div>

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <AlertTriangle size={20} className="text-orange-400" />
              Active Alerts ({alerts.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={`p-4 rounded-lg border ${getSeverityColor(alert.severity)}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{alert.icon}</span>
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">{alert.message}</h3>
                      <p className="text-sm opacity-80">{alert.action}</p>
                      {alert.platform && (
                        <span className="inline-block mt-2 px-2 py-0.5 bg-black/20 rounded text-xs">
                          {alert.platform}
                        </span>
                      )}
                      
                      {/* Action buttons */}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => {
                            if (alert.action_url) {
                              window.location.assign(alert.action_url);
                              return;
                            }
                            toast.success('Alert reviewed');
                            // Optionally remove after review
                            setAlerts(alerts.filter((_, idx) => idx !== i));
                          }}
                          className="px-3 py-1 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded text-green-400 text-xs font-medium transition"
                        >
                          ✓ Review
                        </button>
                        <button
                          onClick={() => {
                            toast.info('Alert postponed for 24h');
                            // Remove from current view (would reappear after 24h in real implementation)
                            setAlerts(alerts.filter((_, idx) => idx !== i));
                          }}
                          className="px-3 py-1 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/30 rounded text-amber-400 text-xs font-medium transition"
                        >
                          ⏰ Postpone
                        </button>
                        <button
                          onClick={() => {
                            setAlerts(alerts.filter((_, idx) => idx !== i));
                            toast.success('Alert dismissed');
                          }}
                          className="px-3 py-1 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded text-red-400 text-xs font-medium transition"
                        >
                          ✕ Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Strategy Brief */}
        {briefData && (
          <div className="mb-6">
            <div className="bg-gradient-to-br from-indigo-900/20 to-blue-900/20 border border-indigo-500/20 rounded-xl p-8 shadow-lg shadow-indigo-500/5">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold flex items-center gap-3 bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">
                  <TrendingUp size={28} className="text-indigo-400" />
                  Daily Strategy Brief
                </h2>
                <span className="text-sm text-zinc-300 bg-indigo-900/20 border border-indigo-500/20 px-4 py-2 rounded-lg font-medium">
                  {new Date(briefData.generated_at).toLocaleDateString()} · {new Date(briefData.generated_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                </span>
              </div>

              {/* AI Brief Content - Enhanced Readability */}
              <div className="prose prose-invert prose-lg max-w-none">
                <div className="text-base leading-relaxed text-zinc-100" style={{ lineHeight: '1.9', letterSpacing: '0.015em' }}>
                  <ReactMarkdown 
                    components={{
                      p: ({node, ...props}) => <p className="mb-4 text-zinc-200" {...props} />,
                      h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-white mb-4 mt-6" {...props} />,
                      h2: ({node, ...props}) => <h2 className="text-xl font-bold text-indigo-300 mb-3 mt-5" {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-lg font-semibold text-blue-300 mb-2 mt-4" {...props} />,
                      strong: ({node, ...props}) => <strong className="text-white font-bold" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 space-y-2" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-4 space-y-2" {...props} />,
                      li: ({node, ...props}) => <li className="text-zinc-200 ml-2" {...props} />,
                    }}
                  >
                    {briefData.brief}
                  </ReactMarkdown>
                </div>
              </div>

              {/* Business Intel Summary */}
              <div className="mt-6 pt-6 border-t border-purple-800/30">
                <h3 className="text-sm font-bold mb-3 text-purple-300">Business Intel Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <Stat label="Total Videos" value={briefData.intel.total_videos} />
                  <Stat label="Store Enabled" value={briefData.intel.store_videos_count} />
                  <Stat label="Recent Posts (7d)" value={briefData.intel.recent_posts_count} />
                  <Stat label="Needs Edit" value={briefData.intel.videos_needing_edit} color="text-orange-400" />
                </div>
                {(briefData.intel.jff_mass_message_reports || []).map((report) => (
                  <div key={`${report.account_id}:${report.range}`} className="mt-4 border-t border-zinc-800 pt-3" data-testid="ai-manager-jff-mass-message-report">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h4 className="text-xs font-semibold text-amber-300">JFF Mass Message Media · {report.range_label}</h4>
                      <span className="text-[10px] text-zinc-500">{report.account_id} · captured {new Date(report.captured_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                      <Stat label="Mass messages" value={formatCountReport(report.metrics?.mass_messages?.value)} />
                      <Stat label="Recipients" value={formatCountReport(report.metrics?.recipients?.value)} />
                      <Stat label="Purchases" value={formatCountReport(report.metrics?.purchases?.value)} />
                      <Stat label="Reported earned · separate" value={formatCurrencyReport(report.metrics?.reported_earned?.value, report.metrics?.reported_earned?.currency)} />
                    </div>
                    <p className="mt-2 text-[10px] text-zinc-500">Platform-reported campaign summary; not attributed to individual fans and not added to Sales or payout totals.</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Weekly and monthly planning */}
        <section className="mb-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="ai-manager-plans">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-bold"><Calendar size={20} className="text-cyan-400" /> Plans and promotions</h2>
              <p className="mt-1 text-sm text-zinc-400">Recommendations become calendar and post drafts only after approval. Nothing is published automatically.</p>
            </div>
            <div className="flex items-center gap-2">
              <div role="group" aria-label="Plan period" className="inline-flex rounded-md border border-zinc-700 p-0.5">
                {["weekly", "monthly"].map((period) => (
                  <button
                    key={period}
                    type="button"
                    aria-pressed={planPeriod === period}
                    onClick={() => setPlanPeriod(period)}
                    className={`rounded px-3 py-1.5 text-sm capitalize ${planPeriod === period ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-white"}`}
                  >
                    {period}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={generatePlan}
                disabled={generatingPlan}
                className="inline-flex items-center gap-2 rounded-md bg-cyan-700 px-3 py-2 text-sm font-medium hover:bg-cyan-600 disabled:opacity-50"
              >
                <Sparkles size={15} /> {generatingPlan ? "Planning…" : "Generate plan"}
              </button>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {planProposals.length === 0 && <p className="py-5 text-center text-sm text-zinc-500">No plans yet.</p>}
            {planProposals.map((proposal) => (
              <article key={proposal.id} className="rounded-md border border-zinc-800 bg-zinc-950 p-4" data-testid={`ai-plan-${proposal.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold capitalize text-zinc-100">{proposal.period} plan</h3>
                      <span className={`rounded px-2 py-0.5 text-xs ${proposal.status === "pending_approval" ? "bg-amber-900/40 text-amber-300" : proposal.status === "approved" ? "bg-emerald-900/40 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>
                        {proposal.status?.replaceAll("_", " ")}
                      </span>
                      <span className="text-xs text-zinc-500">{proposal.start_date} to {proposal.end_date}</span>
                    </div>
                    {proposal.summary && <p className="mt-2 max-w-3xl whitespace-pre-wrap text-sm text-zinc-300">{proposal.summary}</p>}
                  </div>
                  {proposal.status === "pending_approval" && (
                    <div className="flex gap-2">
                      <button type="button" onClick={() => actOnPlan(proposal, "approve")} disabled={planActionId === proposal.id} className="inline-flex items-center gap-1.5 rounded bg-emerald-700 px-3 py-2 text-xs font-semibold hover:bg-emerald-600 disabled:opacity-50">
                        <CheckCircle size={14} /> Approve drafts
                      </button>
                      <button type="button" onClick={() => actOnPlan(proposal, "reject")} disabled={planActionId === proposal.id} className="rounded border border-zinc-700 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50">Reject</button>
                    </div>
                  )}
                </div>
                {(proposal.calendar_items?.length > 0 || proposal.promos?.length > 0) && (
                  <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {proposal.calendar_items?.length > 0 && (
                      <div>
                        <h4 className="mb-2 text-xs font-semibold uppercase text-cyan-300">Calendar proposals</h4>
                        <ul className="space-y-1.5 text-sm text-zinc-300">
                          {proposal.calendar_items.map((item, index) => <li key={`${item.date}-${index}`}>{item.date} · {item.platform || "Any platform"} · {item.title}</li>)}
                        </ul>
                      </div>
                    )}
                    {proposal.promos?.length > 0 && (
                      <div>
                        <h4 className="mb-2 text-xs font-semibold uppercase text-pink-300">Promotion drafts</h4>
                        <div className="space-y-2">
                          {proposal.promos.map((promo, index) => (
                            <div key={`${promo.platform}-${index}`} className="rounded border border-zinc-800 p-2.5 text-sm">
                              <div className="text-xs text-zinc-500">{promo.date || "Unscheduled draft"} · {promo.platform || "Platform TBD"} · {promo.kind || "Promotion"}</div>
                              {promo.linked_item && <div className="mt-1 text-zinc-300">{promo.linked_item}</div>}
                              {promo.offer_idea && <div className="mt-1 text-zinc-300">Offer idea: {promo.offer_idea}</div>}
                              {promo.draft_copy && <p className="mt-1 whitespace-pre-wrap text-zinc-100">{promo.draft_copy}</p>}
                              {promo.rationale && <p className="mt-1 text-xs text-zinc-500">{promo.rationale}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {proposal.actions?.length > 0 && <p className="mt-3 text-xs text-zinc-400">Actions: {proposal.actions.join(" · ")}</p>}
              </article>
            ))}
          </div>
        </section>

        {/* Content Suggestions */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Target size={20} className="text-blue-400" />
              AI Content Suggestions
            </h2>
            <button
              onClick={generateSuggestions}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition"
            >
              Generate Ideas
            </button>
          </div>

          {suggestions ? (
            <div className="prose prose-invert prose-sm max-w-none text-zinc-300 whitespace-pre-wrap">
              {suggestions.suggestions}
              <p className="text-xs text-zinc-500 mt-4">
                Generated: {new Date(suggestions.generated_at).toLocaleString()}
              </p>
            </div>
          ) : (
            <p className="text-zinc-400 text-center py-8">
              Click &quot;Generate Ideas&quot; to get AI-powered content suggestions based on your catalog and trends.
            </p>
          )}
        </div>

        {/* Tweet Reposting with AI - Integrated with X Twitter Hub */}
        <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border border-blue-500/30 rounded-xl p-6 mt-6 shadow-lg shadow-blue-500/10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <Twitter size={24} className="text-blue-400" />
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Tweet Reposting Manager
                </span>
              </h2>
              <p className="text-sm text-zinc-400 mt-1 flex items-center gap-2">
                <span>Powered by X Twitter Hub</span>
                <a 
                  href="/platform-hubs/x"
                  className="text-blue-400 hover:text-blue-300 underline text-xs"
                >
                  → Open X Hub
                </a>
              </p>
            </div>
            <button
              onClick={loadTweetSuggestions}
              disabled={loadingTweets}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:opacity-50 rounded-lg text-sm font-semibold transition flex items-center gap-2 shadow-lg shadow-blue-500/20"
            >
              {loadingTweets ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Twitter size={14} />
                  Analyze Top Tweets
                </>
              )}
            </button>
          </div>

          <p className="text-zinc-400 text-sm mb-4">
            AI analyzes your best-performing tweets and suggests fresh captions for reposting. Review and approve before posting.
          </p>

          {tweetSuggestions.length > 0 ? (
            <div className="space-y-4">
              {tweetSuggestions.map((tweet) => (
                <div
                  key={tweet.tweet_id}
                  className="border border-zinc-800 rounded-lg p-4 bg-zinc-950/50 hover:border-zinc-700 transition"
                >
                  {/* Original Tweet */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-zinc-500 font-medium">ORIGINAL TWEET</span>
                      <span className="text-xs text-zinc-600">{formatDate(tweet.created_at)}</span>
                    </div>
                    <p className="text-sm text-zinc-300 mb-3">{tweet.original_text}</p>
                    
                    {/* Engagement Metrics */}
                    <div className="flex items-center gap-4 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Heart size={12} className="text-red-400" />
                        {tweet.likes}
                      </span>
                      <span className="flex items-center gap-1">
                        <Repeat size={12} className="text-green-400" />
                        {tweet.retweets}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle size={12} className="text-blue-400" />
                        {tweet.replies}
                      </span>
                      <span className="ml-auto text-purple-400 font-semibold">
                        {tweet.engagement} total engagement
                      </span>
                    </div>
                  </div>

                  {/* AI Reasoning */}
                  <div className="mb-4 p-3 bg-purple-950/20 border border-purple-800/30 rounded-lg">
                    <p className="text-xs text-purple-300">
                      <strong className="text-purple-400">Why repost:</strong> {tweet.reasoning}
                    </p>
                  </div>

                  {/* Suggested New Caption */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={14} className="text-yellow-400" />
                      <span className="text-xs text-zinc-500 font-medium">AI-GENERATED NEW CAPTION</span>
                    </div>
                    <div className="p-3 bg-zinc-900 border border-zinc-800 rounded-lg">
                      <p className="text-sm text-zinc-100">{tweet.suggested_caption}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleRepostTweet(tweet)}
                      disabled={reposting === tweet.tweet_id}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2"
                    >
                      {reposting === tweet.tweet_id ? (
                        <>
                          <RefreshCw size={14} className="animate-spin" />
                          Saving draft...
                        </>
                      ) : (
                        <>
                          <Send size={14} />
                          Save repost draft
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setTweetSuggestions(prev => prev.filter(t => t.tweet_id !== tweet.tweet_id))}
                      disabled={reposting === tweet.tweet_id}
                      className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg text-sm font-medium transition"
                    >
                      Skip
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Twitter size={48} className="mx-auto mb-4 text-zinc-700" />
              <p className="text-zinc-400 mb-2">
                No tweet suggestions loaded yet
              </p>
              <p className="text-sm text-zinc-500">
                Click &quot;Analyze Top Tweets&quot; to find your best-performing content to repost with fresh captions
              </p>
            </div>
          )}
        </div>

      </div>
    </AppLayout>
  );
}

function Stat({ label, value, color = "text-purple-400" }) {
  return (
    <div>
      <p className="text-xs text-zinc-400 mb-1">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function formatCurrencyReport(value, currency) {
  if (value === null || value === undefined) return "Not reported";
  if (!currency) return `${Number(value).toLocaleString()} · currency unavailable`;
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(Number(value));
}

function formatCountReport(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "Not reported";
  return Number(value).toLocaleString();
}
